import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Pencil, X, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = ["VEPA", "Pergole Bioclimatiche", "Normativa", "Realizzazioni", "Guide"];

const EMPTY = {
  title: "", slug: "", category: "VEPA", excerpt: "", content: "",
  cover_image: "", cover_alt: "", meta_title: "", meta_description: "",
  faqs: [], status: "draft", published_at: "",
};

// ISO <-> datetime-local helpers
const toLocalInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 16) : "");
const toISO = (local) => (local ? new Date(local).toISOString() : null);

export default function BlogManager({ token }) {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} article being edited
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => {
    axios.get(`${API}/admin/blog`, auth).then((r) => setArticles(r.data)).catch(() => toast.error("Errore caricamento articoli"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (a) => {
    setForm({ ...EMPTY, ...a, published_at: toLocalInput(a.published_at) });
    setEditing(a);
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadCover = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/blog/upload-image`, fd, auth);
      set("cover_image", `${process.env.REACT_APP_BACKEND_URL}${data.image_url}`);
      toast.success("Copertina caricata");
    } catch { toast.error("Upload non riuscito"); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    setSaving(true);
    const payload = { ...form, published_at: toISO(form.published_at) };
    try {
      if (editing && editing.id) {
        await axios.put(`${API}/admin/blog/${editing.id}`, payload, auth);
        toast.success("Articolo aggiornato");
      } else {
        await axios.post(`${API}/admin/blog`, payload, auth);
        toast.success("Articolo creato");
      }
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio non riuscito");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo articolo?")) return;
    try { await axios.delete(`${API}/admin/blog/${id}`, auth); toast.success("Eliminato"); load(); }
    catch { toast.error("Eliminazione non riuscita"); }
  };

  const setFaq = (i, k, v) => setForm((f) => ({ ...f, faqs: f.faqs.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)) }));
  const addFaq = () => setForm((f) => ({ ...f, faqs: [...f.faqs, { q: "", a: "" }] }));
  const rmFaq = (i) => setForm((f) => ({ ...f, faqs: f.faqs.filter((_, idx) => idx !== i) }));

  const field = "w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:border-black";

  return (
    <div className="bg-white border border-black/10 mb-10" data-testid="blog-manager">
      <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText strokeWidth={1.2} className="w-4 h-4" />
          <h2 className="text-sm uppercase tracking-[0.2em]">Blog · Articoli</h2>
        </div>
        <button onClick={openNew} data-testid="blog-new-button" className="flex items-center gap-2 text-sm border border-black px-4 py-2 hover:bg-black hover:text-white transition-colors">
          <Plus strokeWidth={1.2} className="w-4 h-4" /> Nuovo articolo
        </button>
      </div>

      {!editing && (
        <div className="divide-y divide-black/5">
          {articles.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground text-sm">Nessun articolo. Creane uno nuovo.</p>
          ) : (
            articles.map((a) => (
              <div key={a.id} data-testid={`blog-row-${a.slug}`} className="flex items-center gap-4 px-6 py-4">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{a.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    /blog/{a.slug} · {a.category} ·{" "}
                    <span className={a.status === "published" ? "text-green-700" : "text-amber-600"}>
                      {a.status === "published" ? "Pubblicato" : "Bozza"}
                    </span>
                  </p>
                </div>
                <button onClick={() => openEdit(a)} data-testid={`blog-edit-${a.slug}`} className="p-2 hover:bg-muted" aria-label="Modifica">
                  <Pencil strokeWidth={1.2} className="w-4 h-4" />
                </button>
                <button onClick={() => remove(a.id)} data-testid={`blog-delete-${a.slug}`} className="p-2 hover:bg-muted text-red-600" aria-label="Elimina">
                  <Trash2 strokeWidth={1.2} className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {editing && (
        <div className="p-6 space-y-4" data-testid="blog-form">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-lg">{editing.id ? "Modifica articolo" : "Nuovo articolo"}</h3>
            <button onClick={close} className="p-2 hover:bg-muted" aria-label="Chiudi"><X strokeWidth={1.2} className="w-4 h-4" /></button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Titolo (H1)</span>
              <input data-testid="blog-field-title" className={field} value={form.title} onChange={(e) => set("title", e.target.value)} />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Slug (URL) — opzionale</span>
              <input data-testid="blog-field-slug" className={field} value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto dal titolo" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Categoria</span>
              <select data-testid="blog-field-category" className={field} value={form.category} onChange={(e) => set("category", e.target.value)}>
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Stato</span>
              <select data-testid="blog-field-status" className={field} value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="draft">Bozza</option>
                <option value="published">Pubblicato</option>
              </select>
            </label>
            <label className="block md:col-span-2">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Data di pubblicazione (lascia vuoto = adesso; futuro = programmata)</span>
              <input type="datetime-local" data-testid="blog-field-date" className={field} value={form.published_at} onChange={(e) => set("published_at", e.target.value)} />
            </label>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Estratto (excerpt)</span>
            <textarea className={field} rows={2} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} />
          </label>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Immagine di copertina (URL)</span>
              <input data-testid="blog-field-cover" className={field} value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} />
              <label className="inline-flex items-center gap-2 mt-2 text-xs cursor-pointer border border-black/15 px-3 py-1.5 hover:bg-muted">
                <Upload strokeWidth={1.2} className="w-3.5 h-3.5" /> Carica immagine
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadCover(e.target.files[0])} />
              </label>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Testo ALT copertina</span>
              <input data-testid="blog-field-alt" className={field} value={form.cover_alt} onChange={(e) => set("cover_alt", e.target.value)} />
              {form.cover_image && <img src={form.cover_image} alt="" className="mt-2 h-24 w-full object-cover border border-black/10" />}
            </label>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Meta Title (SEO)</span>
              <input className={field} value={form.meta_title} onChange={(e) => set("meta_title", e.target.value)} placeholder="default = titolo" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Meta Description (SEO)</span>
              <input className={field} value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} placeholder="default = estratto" />
            </label>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Contenuto (Markdown)</span>
            <textarea data-testid="blog-field-content" className={`${field} font-mono`} rows={14} value={form.content} onChange={(e) => set("content", e.target.value)} />
          </label>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">FAQ</span>
              <button onClick={addFaq} data-testid="blog-add-faq" className="text-xs flex items-center gap-1 border border-black/15 px-2 py-1 hover:bg-muted"><Plus className="w-3 h-3" /> Aggiungi</button>
            </div>
            {form.faqs.map((f, i) => (
              <div key={i} className="border border-black/10 p-3 mb-2 space-y-2">
                <div className="flex gap-2">
                  <input className={field} placeholder="Domanda" value={f.q} onChange={(e) => setFaq(i, "q", e.target.value)} />
                  <button onClick={() => rmFaq(i)} className="p-2 text-red-600 hover:bg-muted"><Trash2 className="w-4 h-4" /></button>
                </div>
                <textarea className={field} rows={2} placeholder="Risposta" value={f.a} onChange={(e) => setFaq(i, "a", e.target.value)} />
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={saving} data-testid="blog-save-button" className="bg-black text-white px-6 py-3 text-sm uppercase tracking-wide hover:bg-champagne transition-colors disabled:opacity-50">
              {saving ? "Salvataggio..." : "Salva"}
            </button>
            <button onClick={close} className="border border-black/20 px-6 py-3 text-sm uppercase tracking-wide hover:bg-muted transition-colors">Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}
